import { definedMap } from "collection-utils";
import AppIcon from "common/app-icon";
import type { AppKind } from "common/components/app-kind";
import { loadedDefinedMapWithDefault } from "common/components/component-helpers";
import type { Icon } from "common/components/icons";
import { formatLocalizedString, getLocalizedString } from "common/components/localization";
import type { DropImageHandler, ImageSource } from "common/components/portable-renderers";
import { firstImageURL } from "common/components/portable-renderers";
import type { Loading } from "common/components/types";
import { LOADING } from "common/components/types";
import { logInfo } from "common/debug-print";
import {
    defined,
    definedMapWithDefault,
    getEmoji,
    isEmoji,
    isEmptyOrUndefined,
    isEmptyOrUndefinedish,
    nullToUndefined,
    safeURL,
    sleep,
} from "common/Support";
import deepEqual from "deep-equal";
import { ListItemAccessoryPosition, ListItemFlags } from "generator/array-screens/list-item-flags";
import copy from "lib/copy-to-clipboard";
import * as React from "react";
import { TailwindThemeProvider } from "sharedUI/lib/tailwind-theme-provider";
import { mergeTheme } from "sharedUI/lib/theme-utils";
import { withTheme } from "styled-components";
import { hasFlag } from "webapp/lib/has-flag";
import { trackLinkClick } from "../../../../analytics/link-clicks";
import { longPressTargetClass } from "../../../../lib/long-press-event";
import { getUrlHandling, UrlHandling } from "../../../lib/link-utils";
import type { ClickCallback, MenuItem } from "../../../lib/menu-item";
import { spanBreak } from "../../../lib/span-break";
import type { OverlayTheme } from "../../../lib/theme/overlay-theme";
import type { RuntimeTheme } from "../../../lib/theme/theme";
import {
    bigCaptionListItemOverlay,
    largeListItemOverlay,
} from "../../../lib/theme/theme-overlays/large-list-item-overlay";
import { listOverlay } from "../../../lib/theme/theme-overlays/list-overlay";
import { smallListItemOverlay } from "../../../lib/theme/theme-overlays/small-list-item-overlay";
import LongPressActionBubble from "../../smart/long-press-action-bubble/long-press-action-bubble";
import LongPressDualActionBubble from "../../smart/long-press-action-bubble/long-press-action-dual-bubble";
import TextExpander from "../../smart/text-expander/text-expander";
import {
    EmojiDrawing,
    LinkArea,
    LinkCopyWrapper,
    ListItemImageStyle,
    ListItemStyle,
    SkeletonItem,
    SolidIconStyle,
} from "./list-item-view-style";
import type { Corners } from "./list-item-view-types";

export interface ListItemProps {
    // required props
    readonly appEnvironment: {
        appKind: AppKind;
        appID?: string;
    };

    // optional props
    readonly title?: string | Loading;
    readonly subtitle?: string | Loading;
    readonly caption?: string | Loading;
    readonly imageSource?: ImageSource;
    readonly fallbackInitials?: string;
    readonly targetUrl?: string;
    readonly menuItems?: MenuItem[];
    readonly flags?: ListItemFlags;
    readonly corners?: Corners;
    readonly children?: React.ReactNode;
    readonly accessoryPosition?: ListItemAccessoryPosition;
    readonly itemID?: string;
    readonly disableChevon?: boolean;
    readonly touched?: boolean;
    readonly showActionBubble?: boolean;
    readonly clickable?: boolean;

    // actions
    readonly onClick?: (handled: boolean, event?: React.MouseEvent) => void;
    readonly onDropImage?: DropImageHandler;
    readonly onTouchChanged?: (touched: boolean) => void;
    readonly onLongPressChanged?: (pressed: boolean) => void;
}

interface Props extends ListItemProps {
    readonly theme: RuntimeTheme;
}

class ListItemImpl extends React.Component<Props> {
    private isLink = false;
    private href: string | undefined;
    private hrefIsBrowsable = false;
    private wrapperRef = React.createRef<HTMLDivElement>();

    constructor(props: Props) {
        super(props);
        this.determineLinkStatus(false);
    }

    public shouldComponentUpdate(nextProps: Readonly<Props>): boolean {
        if (
            nextProps.title !== this.props.title ||
            nextProps.subtitle !== this.props.subtitle ||
            nextProps.caption !== this.props.caption ||
            !deepEqual(nextProps.imageSource, this.props.imageSource) ||
            nextProps.fallbackInitials !== this.props.fallbackInitials ||
            nextProps.targetUrl !== this.props.targetUrl ||
            nextProps.theme !== this.props.theme ||
            nextProps.flags !== this.props.flags ||
            nextProps.touched !== this.props.touched ||
            nextProps.showActionBubble !== this.props.showActionBubble ||
            (nextProps.children === undefined) !== (this.props.children === undefined)
        ) {
            // OnClick can vary, output wont change
            return true;
        }

        if (nextProps.menuItems !== this.props.menuItems) {
            const nextCount = definedMapWithDefault(nextProps.menuItems, 0, mi => mi.length);
            const thisCount = definedMapWithDefault(this.props.menuItems, 0, mi => mi.length);

            if (nextCount !== thisCount) {
                return true;
            }
        }

        return false;
    }

    private onTouchStart = (_ev: React.TouchEvent<HTMLDivElement>) => {
        if (this.props.onTouchChanged === undefined) return;
        this.props.onTouchChanged(true);
    };

    private onTouchEnd = (_ev: React.TouchEvent<HTMLDivElement>) => {
        if (this.props.onTouchChanged === undefined) return;
        this.props.onTouchChanged(false);
    };

    private onClick = (ev: React.MouseEvent<HTMLDivElement>) => {
        if (this.props.onClick !== undefined) {
            this.props.onClick(false, ev);
        }
    };

    private onLinkClick = (handled: boolean) => {
        if (this.props.onClick !== undefined) {
            this.props.onClick(handled, undefined);
        }
    };

    private get ref(): React.RefObject<HTMLElement> {
        return this.wrapperRef;
    }

    private determineLinkStatus(canUpdate: boolean) {
        const { targetUrl } = this.props;
        const { href, isLink } = this;
        this.isLink = targetUrl !== undefined;
        this.href = safeURL(targetUrl);
        this.hrefIsBrowsable =
            this.isLink && this.href !== undefined && (this.href.startsWith("http:") || this.href.startsWith("https:"));

        if (canUpdate && (this.href !== href || this.isLink !== isLink)) {
            this.forceUpdate();
        }
    }

    public componentDidMount() {
        const elem = this.ref.current;
        if (elem !== null) {
            elem.addEventListener("long-press", this.onLongPress);
        }
    }

    public componentDidUpdate(prevProps: Props) {
        const { targetUrl } = this.props;
        if (prevProps.targetUrl === targetUrl) return;
        this.determineLinkStatus(true);
    }

    private get isInteractive(): boolean {
        const { flags } = this.props;
        return !hasFlag(flags, ListItemFlags.Inert);
    }

    private get textForShareSheet(): string | undefined {
        const { title, subtitle } = this.props;
        const maybeText = this.invertTitleAndSubtitle ? title : subtitle;
        return maybeText === null ? undefined : maybeText;
    }

    private get canShowShareSheet(): boolean {
        return this.hrefIsBrowsable && (navigator as any).share !== undefined;
    }

    private onLongPress = async () => {
        const { menuItems, flags } = this.props;
        if (hasFlag(flags, ListItemFlags.DisableLongPress) || this.props.onLongPressChanged === undefined) return;
        if (this.isInteractive && (menuItems === undefined || menuItems.length === 0)) return;
        this.props.onLongPressChanged(true);
    };

    private exitLongPress = () => {
        if (this.props.onLongPressChanged === undefined) return;
        this.props.onLongPressChanged(false);
    };

    private get invertTitleAndSubtitle() {
        return hasFlag(this.props.flags, ListItemFlags.InvertTitleAndSubtitle);
    }

    private async handleCopyPossiblyShare(canShowShareSheet: boolean) {
        this.exitLongPress();
        await sleep(0); // The copy process sometimes stalls the whole thread,
        // and design _really_ wants the bubble to go away _instantly_.
        // So. We'll just yield some time here.

        const { title, caption } = this.props;
        const { isLink, href, hrefIsBrowsable } = this;

        const copyTitle = title === null || title === undefined ? "" : title;
        const copyCaption = caption === null ? undefined : caption;
        const copyText = copyCaption ?? copyTitle;

        const doCopy = () => copy(isLink && hrefIsBrowsable ? href ?? copyText : copyText);

        if (canShowShareSheet) {
            try {
                await (navigator as any).share({
                    text: this.textForShareSheet,
                    url: defined(href),
                });
            } catch (e: unknown) {
                // AbortError is normal: this means the user has dismissed the share sheet.
                if (e instanceof DOMException && e.name === "AbortError") return;
                logInfo(`Could not show share sheet`, e);
                doCopy();
            }
        } else {
            doCopy();
        }
    }

    private handleCopy = () => this.handleCopyPossiblyShare(false);
    private handleShare = () => this.handleCopyPossiblyShare(true);

    private actionBubble(ref: React.RefObject<HTMLElement>): React.ReactNode {
        const { appKind } = this.props.appEnvironment;
        if (this.canShowShareSheet) {
            return (
                <LongPressDualActionBubble
                    target={ref}
                    leftLabel={getLocalizedString("copyGeneral", appKind)}
                    rightLabel={getLocalizedString("shareLink", appKind)}
                    onLeftClick={this.handleCopy}
                    onRightClick={this.handleShare}
                    onClose={this.exitLongPress}
                />
            );
        } else {
            const { title, subtitle } = this.props;
            const label = loadedDefinedMapWithDefault(
                this.invertTitleAndSubtitle ? subtitle : title,
                getLocalizedString("copyGeneral", appKind),
                s => formatLocalizedString("copySpecific", [s])
            );
            return (
                <LongPressActionBubble
                    label={label}
                    target={ref}
                    onClick={this.handleCopy}
                    onClose={this.exitLongPress}
                />
            );
        }
    }

    public render(): React.ReactNode {
        const {
            title,
            subtitle,
            imageSource,
            fallbackInitials,
            corners,
            caption,
            flags,
            theme,
            itemID,
            disableChevon,
        } = this.props;
        const { appKind } = this.props.appEnvironment;

        const { href } = this;
        const style = this.invertTitleAndSubtitle ? "inverted" : "normal";
        let menuItems = this.props.menuItems;
        const accessoryPosition = this.props.accessoryPosition ?? ListItemAccessoryPosition.Left;

        let navigation = false;
        if (
            this.isInteractive &&
            menuItems === undefined &&
            !hasFlag(flags, ListItemFlags.DisableChevron) &&
            !disableChevon
        ) {
            menuItems = [
                {
                    title: "chevron",
                    icon: "chevron-right",
                },
            ];
            navigation = true;
        }

        const convertNewlines = hasFlag(flags, ListItemFlags.ConvertNewlines);

        let titleStyle = "textStyle";
        let subtitleStyle = "textDetailStyle";
        if (style === "normal" && !isEmptyOrUndefinedish(subtitle)) {
            titleStyle += " topItem";
        } else if (style !== "normal" && !isEmptyOrUndefinedish(title)) {
            subtitleStyle += " topItem";
        }

        let titleContent: React.ReactNode;
        let label: string | undefined;
        const labelFor = definedMap(nullToUndefined(subtitle), t => `list-item-lf-${t}`);
        if (title === LOADING) {
            titleContent = <SkeletonItem height={`${theme.listItem.titleLineHeight}px`} width="80%" marginBottom="4" />;
        } else {
            titleContent = (
                <div id={labelFor} data-test="list-item-title" dir="auto" className={titleStyle}>
                    {convertNewlines ? spanBreak(title) : title}
                </div>
            );
            label = title;
        }

        let captionContent: React.ReactNode;
        if (caption !== LOADING && !isEmptyOrUndefined(caption)) {
            captionContent = (
                <div data-test="list-item-caption" dir="auto" className="textCaptionStyle" id={labelFor}>
                    {convertNewlines ? spanBreak(caption) : caption}
                </div>
            );
        }

        let imgUrl: string | undefined;
        if (imageSource !== undefined) {
            imgUrl = firstImageURL(imageSource, { thumbnail: true }, this.props.appEnvironment?.appID);
        }

        let iconNode: React.ReactNode;

        if (menuItems !== undefined) {
            iconNode = menuItems.map(mi => (
                <ListItemIcon
                    key={mi.title}
                    theme={this.props.theme}
                    title={mi.title}
                    icon={mi.icon}
                    navigation={navigation}
                    href={mi.href}
                    callback={mi.onClick}
                />
            ));
        }

        const reserveImageSpace = hasFlag(flags, ListItemFlags.ReserveImageSpace);

        const image =
            imgUrl !== undefined || fallbackInitials !== undefined || reserveImageSpace ? (
                <ListItemImage
                    onDropImage={this.props.onDropImage}
                    src={imgUrl}
                    initials={fallbackInitials}
                    reserve={reserveImageSpace}
                />
            ) : null;
        const moveImageRight = hasFlag(flags, ListItemFlags.SwapImageSide);

        let minHeight = theme.listItem.titleLineHeight + theme.componentSmallInnerVerticalPadding * 2;
        const horizontal = hasFlag(flags, ListItemFlags.HorizontalLayout);
        if (!horizontal && hasFlag(flags, ListItemFlags.ReserveDetailSpace)) {
            minHeight += 2 + theme.listItem.detailLineHeight;
        }

        if (!horizontal && hasFlag(flags, ListItemFlags.ReserveCaptionSpace)) {
            minHeight += 4 + theme.listItem.captionLineHeight;
        }

        minHeight = Math.max(minHeight, 44);

        const hasLeftAccessory =
            accessoryPosition === ListItemAccessoryPosition.Left && this.props.children !== undefined;
        const hasTopRightAccessory =
            accessoryPosition === ListItemAccessoryPosition.TopRight && this.props.children !== undefined;
        const hasRightAccessory =
            accessoryPosition === ListItemAccessoryPosition.Right && this.props.children !== undefined;

        let subtitleNode: React.ReactNode;
        if (subtitle === LOADING) {
            subtitleNode = <SkeletonItem height={`${theme.listItem.detailLineHeight}px`} />;
        } else {
            const emoji = subtitle !== undefined && isEmoji(subtitle);
            if (emoji) {
                subtitleStyle += " emoji";
            }
            subtitleNode = (
                <label htmlFor={labelFor} data-test="list-item-subtitle" dir="auto" className={subtitleStyle}>
                    {convertNewlines ? spanBreak(subtitle) : subtitle}
                </label>
            );
        }

        if (hasFlag(flags, ListItemFlags.TruncateWrap)) {
            titleContent = (
                <TextExpander maxLines={4} lineHeight={theme.listItem.titleLineHeight} appKind={appKind}>
                    {titleContent}
                </TextExpander>
            );
        }

        let className = longPressTargetClass;

        if (this.isInteractive) {
            className += " interactive";
        }
        if (this.props.touched === true || this.props.showActionBubble === true) {
            className += " touched";
        }
        if (hasFlag(this.props.flags, ListItemFlags.WrapText)) {
            className += " allow-wrapping";
        }
        if (moveImageRight) {
            className += " move-image-right";
        }
        if (hasFlag(this.props.flags, ListItemFlags.DrawSeparator)) {
            className += " separator";
        }
        if (hasLeftAccessory) {
            className += " no-left-pad";
        }
        if (hasFlag(flags, ListItemFlags.LightWeight)) {
            className += " normal-weight";
        }
        if (itemID !== undefined) {
            className += ` selected-item-list-item-${itemID}`;
        }

        let linkArea: React.ReactNode;

        const useLinkArea = this.isLink && href !== undefined && !hasLeftAccessory;
        // Disallow using link area when left accessory in use as its probably a checkbox
        if (useLinkArea && href !== undefined) {
            const handling = getUrlHandling(href, false);
            const useTag = handling === UrlHandling.UseATag || handling === UrlHandling.UseATagWithBlank;
            const target = handling === UrlHandling.UseATagWithBlank ? "_blank" : undefined;
            linkArea = (
                <LinkArea
                    aria-label={label}
                    onClick={() => {
                        trackLinkClick(href);
                        this.onLinkClick(useTag);
                    }}
                    href={useTag ? href : undefined}
                    target={target}
                />
            );
        }

        const result = (
            <ListItemStyle
                data-test="list-item"
                role={this.props.clickable ? "button" : undefined}
                // The type definitions continue to be broken.
                // StyledComponents really wants a `string & (instance: HTMLDivElement | null) => void` for inane reasons.
                // Too bad. They're getting an `(instance: HTMLDivElement | null) => void` and they're going to like it.
                ref={this.ref as any}
                minHeight={minHeight}
                onClick={useLinkArea ? undefined : this.onClick}
                customCorners={corners}
                onTouchStart={this.onTouchStart}
                onTouchEnd={this.onTouchEnd}
                as={
                    this.props.onClick === undefined && this.props.children !== undefined ? ("label" as any) : undefined
                }
                onTouchCancel={this.onTouchEnd}
                className={className}>
                {linkArea}
                {hasLeftAccessory && this.props.children}
                {!moveImageRight && image}
                <div className={horizontal ? "horizontalTextContainer" : "textContainer"}>
                    {style === "normal" && titleContent}
                    {subtitleNode}
                    {style === "inverted" && titleContent}
                    {captionContent}
                </div>
                {moveImageRight && image}
                {iconNode}
                {hasTopRightAccessory && <div className="ali-accessory-topright">{this.props.children}</div>}
                {hasRightAccessory && <div className="ali-accessory-right">{this.props.children}</div>}
                {this.props.showActionBubble === true && this.actionBubble(this.ref)}
            </ListItemStyle>
        );

        const themeList: OverlayTheme[] = [];

        if (hasFlag(this.props.flags, ListItemFlags.LargeImage)) {
            themeList.push(largeListItemOverlay);
        }

        if (hasFlag(this.props.flags, ListItemFlags.SmallImage)) {
            themeList.push(smallListItemOverlay);
        }

        if (hasFlag(this.props.flags, ListItemFlags.BigCaption)) {
            themeList.push(bigCaptionListItemOverlay);
        }

        if (hasFlag(this.props.flags, ListItemFlags.ForceListStyle)) {
            themeList.push(listOverlay);
        }

        if (themeList.length === 0) return result;

        const newTheme = mergeTheme(this.props.theme, themeList);
        return <TailwindThemeProvider theme={newTheme}>{result}</TailwindThemeProvider>;
    }
}

function ListItemIcon({
    icon,
    navigation,
    theme,
    title,
    href,
    callback,
}: {
    icon: Icon;
    navigation: boolean;
    theme: RuntimeTheme;
    title: string;
    href?: string;
    callback?: ClickCallback;
}): JSX.Element {
    if (navigation) {
        return (
            <div className="navigationIcon">
                <AppIcon icon={icon} size={12} />
            </div>
        );
    }
    const result = (
        <SolidIconStyle isLast={true}>
            <AppIcon icon={icon} size={theme.listItem.actionImage.iconSize} />
        </SolidIconStyle>
    );
    href = safeURL(href);
    if (href === undefined || callback === undefined) {
        return result;
    }
    // FIXME: This callbacks should not require an href implicitly.
    const handling = getUrlHandling(href, false);
    const useTag = handling === UrlHandling.UseATag || handling === UrlHandling.UseATagWithBlank;
    const target = handling === UrlHandling.UseATagWithBlank ? "_blank" : undefined;
    return (
        <LinkCopyWrapper
            data-test="list-item-link-copy"
            aria-label={title}
            onClick={ev => {
                trackLinkClick(defined(href));
                if (useTag) {
                    callback(true);
                } else {
                    callback(false);
                }
                ev.stopPropagation();
            }}
            href={useTag ? href : undefined}
            target={target}>
            {result}
        </LinkCopyWrapper>
    );
}

interface ImageProps {
    src?: string;
    initials?: string;
    onDropImage?: DropImageHandler;
    reserve: boolean;
}

const ListItemImage = ({ src, initials, onDropImage, reserve }: ImageProps) => {
    const fallback = <div className="initialsStyle">{initials}</div>;
    if (src !== undefined) {
        const emoji = getEmoji(src);
        if (emoji !== undefined) {
            return (
                <div data-test="list-item-emoji" className="emojiImageStyle">
                    <EmojiDrawing str={emoji} />
                </div>
            );
        } else {
            return (
                <ListItemImageStyle
                    data-test="list-item-image"
                    className="imageBaseStyle"
                    onDropImage={onDropImage}
                    src={src}
                    alt=""
                    alternate={fallback}
                    altBehavior="both"
                />
            );
        }
    } else if (initials !== undefined) {
        return (
            <ListItemImageStyle
                data-test="list-item-image"
                className="imageBaseStyle"
                onDropImage={onDropImage}
                alt=""
                alternate={fallback}
                altBehavior="both"
            />
        );
    } else if (reserve) {
        return <div className="emojiImageStyle" />;
    } else {
        // Nothing is rendered if we have neither src nor initials
        return null;
    }
};

const ListItem = withTheme(ListItemImpl);
export default ListItem;
